import React, { useState, useEffect, useCallback } from 'react';
import { microTourConfigs } from '../utils/microTourConfigs';
import {
  HelpCircle,
  X,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Info,
  Sparkles,
  Compass
} from 'lucide-react';

export default function MicroTour({ tourId, isOpen: forcedOpen, onClose: onForcedClose, autoPrompt = true }) {
  const config = microTourConfigs[tourId];
  const storageKey = `pfadi_microtour_completed_${tourId}`;
  const dismissKey = `pfadi_microtour_dismissed_${tourId}`;

  const [isOpen, setIsOpen] = useState(false);
  const [showPromptBanner, setShowPromptBanner] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  // Check if tour should auto-prompt on mount
  useEffect(() => {
    if (!config) return;

    if (forcedOpen !== undefined) {
      setIsOpen(forcedOpen);
      if (forcedOpen) {
        setShowPromptBanner(false);
        setCurrentStep(0);
      }
      return;
    }

    if (!autoPrompt) return;

    try {
      const isCompleted = localStorage.getItem(storageKey) === 'true';
      const isDismissed = localStorage.getItem(dismissKey) === 'true';

      if (!isCompleted && !isDismissed) {
        // Show subtle prompt banner after a brief pause
        const timer = setTimeout(() => {
          setShowPromptBanner(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      console.warn('MicroTour storage check failed:', e);
    }
  }, [tourId, forcedOpen, autoPrompt, config, storageKey, dismissKey]);

  const activeStep = config?.steps?.[currentStep];

  // Update target rect
  const updateRect = useCallback(() => {
    if (!isOpen || !activeStep?.target) {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(activeStep.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right
      });
    } else {
      setTargetRect(null);
    }
  }, [isOpen, activeStep]);

  // Polling for target element
  useEffect(() => {
    if (!isOpen || !activeStep) return;

    let attempts = 0;
    const maxAttempts = 30;
    const pollInterval = setInterval(() => {
      attempts++;
      if (!activeStep.target) {
        setTargetRect(null);
        clearInterval(pollInterval);
        return;
      }

      const el = document.querySelector(activeStep.target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        updateRect();
        clearInterval(pollInterval);
      } else if (attempts >= maxAttempts) {
        setTargetRect(null);
        clearInterval(pollInterval);
      }
    }, 100);

    return () => clearInterval(pollInterval);
  }, [isOpen, currentStep, activeStep, updateRect]);

  // Listen to window resize and scroll
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [isOpen, updateRect]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;

      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep]);

  if (!config) return null;

  const handleStart = () => {
    setShowPromptBanner(false);
    setCurrentStep(0);
    setIsOpen(true);
  };

  const handleDismissPrompt = () => {
    setShowPromptBanner(false);
    try {
      localStorage.setItem(dismissKey, 'true');
    } catch (e) {}
  };

  const handleClose = () => {
    setIsOpen(false);
    onForcedClose?.();
  };

  const handleComplete = () => {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (e) {}
    setIsOpen(false);
    onForcedClose?.();
  };

  const handleNext = () => {
    if (currentStep < config.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // -------------------------------------------------------------
  // PROMPT BANNER (Non-intrusive notification)
  // -------------------------------------------------------------
  if (showPromptBanner && !isOpen) {
    return (
      <div className="my-3 p-3 sm:p-3.5 rounded-2xl bg-gradient-to-r from-primary/10 via-surface-containerHigh to-surface-container border border-primary/25 shadow-sm flex items-center justify-between gap-3 animate-fade-in text-surface-on">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Compass className="w-4 h-4" />
          </div>
          <p className="text-xs sm:text-sm font-medium truncate">
            {config.promptText}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={handleStart}
            className="px-3 py-1.5 rounded-xl bg-primary text-primary-on text-xs font-bold shadow-sm hover:bg-primary/90 transition-all flex items-center gap-1 cursor-pointer hover:scale-[1.02]"
          >
            <span>Tour starten</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleDismissPrompt}
            className="p-1.5 rounded-lg text-surface-onVariant hover:text-surface-on hover:bg-surface-containerHighest transition-colors cursor-pointer"
            title="Nicht mehr anzeigen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (!isOpen || !activeStep) return null;

  // -------------------------------------------------------------
  // CALCULATE POSITION
  // -------------------------------------------------------------
  const popoverWidth = 380;
  const padding = 14;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;

  let popoverStyle = {};
  let arrowDirection = 'up';
  let arrowStyle = {};

  if (!targetRect || activeStep.placement === 'center') {
    popoverStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: `min(${popoverWidth}px, calc(100vw - 32px))`,
      zIndex: 100000
    };
    arrowDirection = 'none';
  } else {
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    let placement = activeStep.placement || 'bottom';

    if (placement === 'bottom' && targetRect.bottom + 280 > viewportHeight) {
      placement = 'top';
    } else if (placement === 'top' && targetRect.top - 280 < 0) {
      placement = 'bottom';
    }

    if (placement === 'bottom') {
      const top = Math.min(targetRect.bottom + padding, viewportHeight - 290);
      const left = Math.max(16, Math.min(viewportWidth - popoverWidth - 16, targetCenterX - popoverWidth / 2));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `min(${popoverWidth}px, calc(100vw - 32px))`,
        zIndex: 100000
      };
      arrowDirection = 'up';
      arrowStyle = {
        left: `${Math.max(20, Math.min(popoverWidth - 30, targetCenterX - left))}px`,
        top: '-8px'
      };
    } else if (placement === 'top') {
      const top = Math.max(16, targetRect.top - 280);
      const left = Math.max(16, Math.min(viewportWidth - popoverWidth - 16, targetCenterX - popoverWidth / 2));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `min(${popoverWidth}px, calc(100vw - 32px))`,
        zIndex: 100000
      };
      arrowDirection = 'down';
      arrowStyle = {
        left: `${Math.max(20, Math.min(popoverWidth - 30, targetCenterX - left))}px`,
        bottom: '-8px'
      };
    } else if (placement === 'right') {
      const left = Math.min(targetRect.right + padding, viewportWidth - popoverWidth - 16);
      const top = Math.max(16, Math.min(viewportHeight - 280, targetCenterY - 100));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `min(${popoverWidth}px, calc(100vw - 32px))`,
        zIndex: 100000
      };
      arrowDirection = 'left';
      arrowStyle = {
        top: `${Math.max(20, Math.min(220, targetCenterY - top))}px`,
        left: '-8px'
      };
    } else if (placement === 'left') {
      const left = Math.max(16, targetRect.left - popoverWidth - padding);
      const top = Math.max(16, Math.min(viewportHeight - 280, targetCenterY - 100));
      popoverStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `min(${popoverWidth}px, calc(100vw - 32px))`,
        zIndex: 100000
      };
      arrowDirection = 'right';
      arrowStyle = {
        top: `${Math.max(20, Math.min(220, targetCenterY - top))}px`,
        right: '-8px'
      };
    }
  }

  const isLastStep = currentStep === config.steps.length - 1;

  return (
    <>
      {/* Spotlight Backdrop */}
      <div className="fixed inset-0 z-[99998] pointer-events-none transition-all duration-300">
        {targetRect ? (
          <div
            className="absolute rounded-2xl ring-2 ring-primary ring-offset-2 ring-offset-black/50 transition-all duration-300 pointer-events-none shadow-[0_0_0_9999px_rgba(0,0,0,0.65)]"
            style={{
              top: `${Math.max(0, targetRect.top - 6)}px`,
              left: `${Math.max(0, targetRect.left - 6)}px`,
              width: `${targetRect.width + 12}px`,
              height: `${targetRect.height + 12}px`
            }}
          />
        ) : (
          <div className="fixed inset-0 bg-black/65 backdrop-blur-xs pointer-events-auto transition-opacity duration-300" onClick={handleClose} />
        )}
      </div>

      {/* Popover Card */}
      <div
        style={popoverStyle}
        className="rounded-3xl bg-surface-containerLowest border border-primary/30 shadow-2xl text-surface-on p-5 animate-scale-in relative pointer-events-auto"
      >
        {/* Pointer Arrow */}
        {arrowDirection === 'up' && (
          <div 
            style={arrowStyle} 
            className="absolute w-4 h-4 bg-surface-containerLowest border-t border-l border-primary/30 rotate-45 transform -translate-x-1/2"
          />
        )}
        {arrowDirection === 'down' && (
          <div 
            style={arrowStyle} 
            className="absolute w-4 h-4 bg-surface-containerLowest border-b border-r border-primary/30 rotate-45 transform -translate-x-1/2"
          />
        )}
        {arrowDirection === 'left' && (
          <div 
            style={arrowStyle} 
            className="absolute w-4 h-4 bg-surface-containerLowest border-b border-l border-primary/30 rotate-45 transform -translate-y-1/2"
          />
        )}
        {arrowDirection === 'right' && (
          <div 
            style={arrowStyle} 
            className="absolute w-4 h-4 bg-surface-containerLowest border-t border-r border-primary/30 rotate-45 transform -translate-y-1/2"
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between gap-2 pb-2.5 border-b border-outlineVariant/20 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary px-2 py-0.5 rounded-full bg-primary/10">
              {config.title} · {currentStep + 1}/{config.steps.length}
            </span>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg text-surface-onVariant hover:text-surface-on hover:bg-surface-containerHigh transition-colors cursor-pointer"
            title="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-2 mb-3.5">
          <h4 className="text-sm sm:text-base font-bold text-surface-on leading-snug">
            {activeStep.title}
          </h4>
          <p className="text-xs text-surface-onVariant leading-relaxed">
            {activeStep.text}
          </p>

          {activeStep.tip && (
            <div className="p-2.5 rounded-xl bg-primary/5 border border-primary/15 text-[11px] font-medium text-surface-on leading-snug flex items-start gap-2 mt-2">
              <Info className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <span className="text-surface-onVariant">{activeStep.tip}</span>
            </div>
          )}
        </div>

        {/* Progress Dots */}
        <div className="flex items-center justify-center gap-1.5 py-1 mb-3">
          {config.steps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentStep(idx)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                idx === currentStep
                  ? 'w-5 bg-primary'
                  : idx < currentStep
                  ? 'w-1.5 bg-primary/40'
                  : 'w-1.5 bg-outlineVariant/40'
              }`}
            />
          ))}
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-between gap-2 pt-2.5 border-t border-outlineVariant/20">
          <button
            onClick={handleClose}
            className="px-2.5 py-1.5 text-[11px] font-semibold text-surface-onVariant hover:text-surface-on transition-colors cursor-pointer"
          >
            Überspringen
          </button>

          <div className="flex items-center gap-1.5">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="p-1.5 rounded-xl bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-on transition-colors cursor-pointer"
                title="Zurück"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}

            <button
              onClick={handleNext}
              className="px-3.5 py-1.5 rounded-xl bg-primary text-primary-on text-xs font-bold shadow-sm hover:bg-primary/90 transition-all flex items-center gap-1 cursor-pointer hover:scale-[1.02]"
            >
              <span>{isLastStep ? 'Fertig' : 'Weiter'}</span>
              {isLastStep ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
